import { useEffect, useState } from 'react';
import { RxReload } from 'react-icons/rx';
import { FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { getAllCompanies, createCompany, updateCompany } from '../../../api/administration';
import CompanyTable from './components/CompanyTable';
import CompanyModal from './components/CompanyModal';

const initialCompany = {
  company: '',
  division: '',
  description: '',
  status: 'ACTIVE',
};

const CompanyManagement = ({ client, onBack }) => {
  const [companies, setCompanies] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setEditMode] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(initialCompany);
  const [isSubmitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(15);

  const clientUid = client?.client_uid;
  const clientRecordId = client?.id;
  const clientLabel = client?.business_name || client?.full_name || client?.client_code || 'Client';

  const getData = async () => {
    console.log('[CompanyManagement] client prop:', client);
    console.log('[CompanyManagement] clientUid:', clientUid, 'clientRecordId:', clientRecordId);
    if (!clientUid) {
      toast.error('Client UID not available — check console for client prop values');
      return;
    }
    setLoading(true);
    try {
      const response = await getAllCompanies(clientUid);
      console.log('[CompanyManagement] API response:', response);
      if (response && response.data) {
        const list = response.data?.response?.data || response.data?.data || [];
        console.log('[CompanyManagement] companies list:', list);
        setCompanies(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error('[CompanyManagement] Error fetching companies:', error);
      toast.error('Something went wrong while fetching companies');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientUid]);

  const openModalForNew = () => {
    setEditMode(false);
    setEditingCompanyId(null);
    setCurrentCompany(initialCompany);
    setModalOpen(true);
  };

  const openModalForEdit = (companyId) => {
    const company = companies.find((c) => c.company_id === companyId);
    if (!company) {
      toast.error('Company not found');
      return;
    }
    setEditMode(true);
    setEditingCompanyId(company.company_id);
    setCurrentCompany({
      company: company.company || '',
      division: company.division || '',
      description: company.description || '',
      status: company.status || 'ACTIVE',
    });
    setModalOpen(true);
  };

  const handleDeleteCompany = async (companyId) => {
    const company = companies.find((c) => c.company_id === companyId);
    if (!company) {
      toast.error('Company not found');
      return;
    }
    try {
      await updateCompany(companyId, { status: 'INACTIVE' });
      toast.success('Company deleted successfully');
      getData();
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to delete company';
      toast.error(msg);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      setSubmitting(true);

      if (isEditMode) {
        const payload = {};
        if (formData.division?.trim()) payload.division = formData.division.trim();
        if (formData.description?.trim()) payload.description = formData.description.trim();
        if (formData.status) payload.status = formData.status;
        console.log('[CompanyManagement] Update payload:', payload, 'companyId:', editingCompanyId);
        await updateCompany(editingCompanyId, payload);
        toast.success('Company updated successfully');
      } else {
        if (!formData.company?.trim()) {
          toast.error('Company Code is required');
          return;
        }
        const createPayload = {
          company: formData.company.trim(),
        };
        if (formData.division?.trim()) createPayload.division = formData.division.trim();
        if (formData.description?.trim()) createPayload.description = formData.description.trim();
        if (formData.status) createPayload.status = formData.status;
        if (clientRecordId) createPayload.client_id = parseInt(clientRecordId, 10);
        if (clientUid) createPayload.client_uid = clientUid;
        console.log('[CompanyManagement] Create payload:', createPayload);
        await createCompany(createPayload);
        toast.success('Company created successfully');
      }

      setModalOpen(false);
      getData();
    } catch (error) {
      console.error('[CompanyManagement] Error saving company:', error);
      console.error('[CompanyManagement] Error response:', error.response?.data);
      const msg = error.response?.data?.message || error.message || 'Failed to save company';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCompanies = companies.filter((c) => {
    if (!searchTerm) return true;
    const lower = searchTerm.toLowerCase();
    return (
      c.company?.toLowerCase().includes(lower) ||
      c.division?.toLowerCase().includes(lower) ||
      c.description?.toLowerCase().includes(lower) ||
      c.status?.toLowerCase().includes(lower)
    );
  });

  return (
    <div className="p-5">
      <div className="border border-gray-300 rounded-lg relative">
        {/* Header */}
        <div className="flex justify-between items-center gap-2 p-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded cursor-pointer flex items-center gap-1 text-secondary-700"
              title="Back to Accounts"
            >
              <FiArrowLeft size={20} />
              <span className="text-sm font-medium">Accounts</span>
            </button>
            <span className="text-gray-400 mx-1">/</span>
            <span className="font-semibold text-secondary-800 text-sm">{clientLabel}</span>
            <button
              onClick={getData}
              className="p-2 hover:bg-gray-100 rounded cursor-pointer ml-2"
              title="Refresh"
              disabled={isLoading}
            >
              <RxReload size={18} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>

          <div className="search-bar width-90">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by company code, division, description..."
              className="search-input border-radius-10 w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <button
            onClick={openModalForNew}
            className="py-3 px-6 bg-secondary-700 text-white rounded-full hover:bg-secondary-800 whitespace-nowrap"
          >
            <span className="mr-3">+</span>
            Add Company
          </button>
        </div>

        {/* Table */}
        <div className="relative">
          <CompanyTable
            data={filteredCompanies}
            onEditCompany={openModalForEdit}
            onDeleteCompany={handleDeleteCompany}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
          />
          {isLoading && (
            <div
              className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
              style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
            >
              <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
              <span className="text-lg text-secondary-700 font-semibold flex items-center">
                Loading
                <span className="inline-flex ml-1">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      <CompanyModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleFormSubmit}
        isEditMode={isEditMode}
        defaultValues={currentCompany}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default CompanyManagement;
